import React, { useMemo } from 'react';
import { useGames } from '@/hooks/useShared';
import { calculateGameStats } from '@/services/insights';
import { InsightsSubNav } from '@/components/insights/InsightsSubNav';
import { formatCurrency } from '@/lib/utils';
import { LoadingSpinner } from '@/components/ui';
import type { GameStatEntry } from '@/lib/types/insights';

export function GameStatsPage() {
  const { games, loading } = useGames();

  const gameStats = useMemo(() => calculateGameStats(games), [games]);
  const highlights = useMemo(() => getHighlights(gameStats), [gameStats]);

  if (loading) return <LoadingSpinner message="Loading game stats..." />;

  return (
    <div className="max-w-[400px] mx-auto">
      <Header title="Game Stats" subtitle="GAME STATISTICS" />

      <InsightsSubNav />

      <Section title="Game Highlights">
        <div style={{ display: 'flex', gap: 6 }}>
          <HighlightCard label="Biggest Pot" value={formatCurrency(highlights.biggestPot)} detail={highlights.biggestPotGame} />
          <HighlightCard label="Most Players" value={String(highlights.mostPlayers)} detail={highlights.mostPlayersGame} />
          <HighlightCard label="Closest Game" value={formatCurrency(highlights.closestSpread)} detail="Winner-Loser spread" />
        </div>
      </Section>

      <Section title="All Games">
        <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 8 }}>Sorted by pot size</div>
        {gameStats.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#6b7280', padding: 40, fontSize: 13 }}>No archived games yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {gameStats.map(game => (
              <GameStatCard key={game.gameId} game={game} />
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function getHighlights(entries: GameStatEntry[]) {
  let biggestPot = 0, biggestPotGame = '';
  let mostPlayers = 0, mostPlayersGame = '';
  let closestSpread = Infinity;

  for (const e of entries) {
    if (e.potSize > biggestPot) { biggestPot = e.potSize; biggestPotGame = `${formatDate(e.date)} · ${e.playerCount} players`; }
    if (e.playerCount > mostPlayers) { mostPlayers = e.playerCount; mostPlayersGame = `${formatDate(e.date)} · ${e.venue}`; }
    if (e.spread < closestSpread && e.spread > 0) closestSpread = e.spread;
  }

  return { biggestPot, biggestPotGame, mostPlayers, mostPlayersGame, closestSpread: closestSpread === Infinity ? 0 : closestSpread };
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function Header({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div style={{ background: '#e8f5f0', border: '1px solid rgba(0,104,85,0.1)', borderRadius: 16, padding: 20, marginBottom: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px', color: 'rgba(0,104,85,0.7)', marginBottom: 4 }}>{subtitle}</div>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#2c2f30', lineHeight: 1.2 }}>{title}</h1>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h2 style={{ fontSize: 12, fontWeight: 700, color: '#006855', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 10 }}>{title}</h2>
      {children}
    </div>
  );
}

function HighlightCard({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <div style={{ flex: 1, background: 'white', borderRadius: 12, padding: '10px 8px', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: '#006855', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 800, color: '#2c2f30' }}>{value}</div>
      <div style={{ fontSize: 8, color: '#6b7280', marginTop: 4 }}>{detail}</div>
    </div>
  );
}

function GameStatCard({ game }: { game: GameStatEntry }) {
  return (
    <div style={{ background: 'white', borderRadius: 12, padding: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#006855' }}>{formatDate(game.date)}</div>
        <div style={{ fontSize: 10, color: '#6b7280' }}>{game.playerCount} players</div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#2c2f30', marginBottom: 6 }}>{game.venue}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <span style={{ fontSize: 10, color: '#6b7280' }}>Pot: </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#2c2f30' }}>{formatCurrency(game.potSize)}</span>
        </div>
        <div style={{ textAlign: 'right' }}>
          {game.winner && (
            <div style={{ fontSize: 10 }}>
              <span style={{ color: '#22c55e' }}> Winner: {game.winner.name}</span>
            </div>
          )}
          {game.biggestLoser && (
            <div style={{ fontSize: 10 }}>
              <span style={{ color: '#ef4444' }}> Loser: {game.biggestLoser.name}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
